import { isAmountPreset } from '../lib/amountPresets';
import { Address } from '../lib/address/Address';
import { floorUsd } from '../components/utils/formatUsdAmount';
import { isDepositAddressFlow } from '../helpers/swapFlow';
import { minimumAmountMessage } from '../lib/amountMessages';
export const FORM_VALIDATION_ERROR_CODES = {
    ROUTE_NOT_FOUND: "ROUTE_NOT_FOUND",
    RATE_NOT_AVAILABLE: "RATE_NOT_AVAILABLE",
    PRICE_IMPACT_TOO_HIGH: "PRICE_IMPACT_TOO_HIGH",
    MIN_AMOUNT_ERROR: "MIN_AMOUNT_ERROR",
    MAX_AMOUNT_ERROR: "MAX_AMOUNT_ERROR",
    INVALID_AMOUNT: "INVALID_AMOUNT",
    NO_SOURCE_ROUTE: "NO_SOURCE_ROUTE",
    INVALID_DESTINATION_ADDRESS: "INVALID_DESTINATION_ADDRESS",
    ADDRESS_UPDATE_REQUIRED: "ADDRESS_UPDATE_REQUIRED",
    MANUAL_TRANSFER_UNSUPPORTED: "MANUAL_TRANSFER_UNSUPPORTED",
};
export function resolveFormValidation({ values, maxAllowedAmount, minAllowedAmount, minAllowedAmountInUsd, maxAllowedAmountInUsd, isUsdMode, sourceAddress, sameAccountNetwork, quoteError, noExchangeWithdrawalRoute }) {
    let amount = values.amount ? Number(values.amount) : undefined;
    // Deposit address flow without exchange: no amount or exchange required
    const depositAddressFlow = isDepositAddressFlow(values.depositMethod, values.fromExchange);
    if (!depositAddressFlow && !values.from && !values.fromExchange) {
        return { message: 'Select source' };
    }
    if (!values.to) {
        return { message: 'Select destination' };
    }
    if (noExchangeWithdrawalRoute) {
        return { message: 'Route not found', code: FORM_VALIDATION_ERROR_CODES.ROUTE_NOT_FOUND };
    }
    if (!depositAddressFlow && !values.fromExchange && !values.fromAsset) {
        return { message: 'Select source asset' };
    }
    if (!values.toAsset) {
        return { message: 'Select destination asset' };
    }
    if (!depositAddressFlow) {
        if (isAmountPreset(values.amountMode) && values.amountPresetError)
            return { message: values.amountPresetError };
        if (amount === undefined || amount === 0 || isNaN(Number(amount))) {
            return { message: 'Enter an amount' };
        }
        if (amount < 0) {
            return { message: "Can't be negative" };
        }
        if (maxAllowedAmount != undefined && amount > maxAllowedAmount) {
            // In USD mode, floor the USD limit so the displayed max stays within the actual limit
            const displayAmount = isUsdMode && maxAllowedAmountInUsd != undefined ? `$${floorUsd(maxAllowedAmountInUsd)}` : maxAllowedAmount;
            return { code: FORM_VALIDATION_ERROR_CODES.MAX_AMOUNT_ERROR, message: `Max amount is ${displayAmount}` };
        }
        if (minAllowedAmount != undefined && amount < minAllowedAmount) {
            return { code: FORM_VALIDATION_ERROR_CODES.MIN_AMOUNT_ERROR, message: minimumAmountMessage(minAllowedAmount, values.fromAsset, minAllowedAmountInUsd, isUsdMode) };
        }
        if (!/^[0-9]*[.,]?[0-9]*$/i.test(values.amount ?? '')) {
            return { message: 'Invalid amount', code: FORM_VALIDATION_ERROR_CODES.INVALID_AMOUNT };
        }
    }
    if (depositAddressFlow) {
        if (!values.from || !values.fromAsset) {
            return { message: 'No source route available', code: FORM_VALIDATION_ERROR_CODES.NO_SOURCE_ROUTE };
        }
        if (!values.destination_address) {
            return { message: 'Enter destination address' };
        }
    }
    if (values.to) {
        if (values.destination_address && !Address.isValid(values.destination_address, values.to)) {
            return { message: `Enter a valid ${values.to?.display_name} address`, code: FORM_VALIDATION_ERROR_CODES.INVALID_DESTINATION_ADDRESS };
        }
    }
    if (!depositAddressFlow) {
        if ((values.from?.name && values.from?.name.toLowerCase() === sameAccountNetwork?.toLowerCase()) ||
            (values.to?.name && values.to?.name.toLowerCase() === sameAccountNetwork?.toLowerCase())) {
            if (sourceAddress &&
                values.destination_address &&
                sourceAddress.toLowerCase() !== values.destination_address.toLowerCase()) {
                return { message: `Address update required`, code: FORM_VALIDATION_ERROR_CODES.ADDRESS_UPDATE_REQUIRED };
            }
            if (values.depositMethod === "deposit_address") {
                return { message: 'Manual Transfer is not supported', code: FORM_VALIDATION_ERROR_CODES.MANUAL_TRANSFER_UNSUPPORTED };
            }
        }
    }
    return resolveQuoteError(quoteError) ?? { message: '' };
}
export function resolveQuoteError(quoteError) {
    const quoteErrorCode = quoteError?.response?.data?.error?.code || quoteError?.code;
    const quoteErrorMessage = quoteError?.response?.data?.error?.message || quoteError?.message;
    if (quoteError && quoteErrorCode !== "QUOTE_REQUIRES_NO_DEPOSIT_ADDRESS") {
        if (quoteErrorCode === "PRICE_IMPACT_TOO_HIGH") {
            return { message: 'Price impact too high', code: FORM_VALIDATION_ERROR_CODES.PRICE_IMPACT_TOO_HIGH };
        }
        if (quoteErrorCode === "ROUTE_NOT_FOUND_ERROR" && quoteErrorMessage === "Rate not available") {
            return { message: 'Rate not available', code: FORM_VALIDATION_ERROR_CODES.RATE_NOT_AVAILABLE };
        }
        return { message: 'Route not found', code: FORM_VALIDATION_ERROR_CODES.ROUTE_NOT_FOUND };
    }
}
